<script>
    import { onMount } from "svelte";
    import {
        NotificationModal,
        PasswordInput,
    } from "../../../Components/Common/index.svelte";
    import { apiMethods } from "../../../Services/index.svelte";

    import {
        resetDefaultStyle,
        getEmailValidation,
        getPasswordValidation,
        getMobileValidation,
        showException,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        addErrorInput,
        removeErrorInput,
    } from "../../PatientEncounter/helpers/encounterUtils.svelte";

    import { DirectUserRoles } from "../../../Utils/utilEnums.svelte";

    export let accountId = Number(0),
        userDetails = {},
        isModalOpen = false,
        closeModal = () => {},
        afterModifyAction = () => {};

    $: isValidEmail = getEmailValidation(userDetails.email);

    $: isValidPwd = getPasswordValidation(userDetails.password);

    $: isValidPhone = getMobileValidation(userDetails.phone);

    $: isPwdMatched =
        userDetails.password && userDetails.confirmPassword
            ? userDetails.password === userDetails.confirmPassword
            : true;

    $: isValidQrCode =
        userDetails.mfa == 4 ? (qrCode ? qrCode.length == 6 : false) : true;

    $: originalUser = { ...userDetails };

    $: isUserDefaultAuthenticatedByGoogle = originalUser?.mfa == 4;
    $: isUserNewAuthenticatedByGoogle = userDetails?.mfa == 4;

    let isModifyingUser = false;

    let isGettingMfaCode = false,
        qrCodeContent = "",
        qrCode = "";

    let errInputs = [];

    let showPassword = false,
        showConfirmPassword = false;

    onMount(() => {
        if (originalUser) userDetails = { ...originalUser };
    });

    const checkInputValidation = async () => {
        errInputs = [];

        _updateInvalidFieldsStyle();

        if (errInputs.length == 0) {
            isModifyingUser = true;

            if (!userDetails.id) await createUser();
            else await saveUser();

            isModifyingUser = false;
        }
    };

    const createUser = async () => {
        userDetails = { ...userDetails, userAccounts: [{ id: accountId }] };

        await apiMethods.postMethod
            .signUp(userDetails)
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        res.error ??
                            "Failed to create user, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    closeModal();
                    afterModifyAction();
                }
            })
            .catch((err) => showException(err));
    };

    const saveUser = async () => {
        // if (isUserNewAuthenticatedByGoogle) await validateMfa();
        // else await updateUser();
        await updateUser();
    };

    const validateMfa = async () => {
        await apiMethods.postMethod
            .validateMfa({ userId: accountId, code: qrCode })
            .then(async (res) => {
                if (res) await updateUser();
                else showError("Incorrect 2FA code");
            })
            .catch((err) => showException(err));
    };

    const updateUser = async () => {
        await apiMethods.putMethod
            .updateUser(userDetails)
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        res.error ??
                            "Failed to create user, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    closeModal();
                    afterModifyAction();
                }
            })
            .catch((err) => showException(err));
    };

    const getMfaQrCode = async () => {
        isGettingMfaCode = true;

        await apiMethods.postMethod
            .getMfaQrCode(userDetails.id)
            .then((res) => {
                if (res) qrCodeContent = res;
                else
                    showError(
                        "Failed to load QR Code, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to load QR Code, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isGettingMfaCode = false;
    };

    // HELPER FUNCTIONS
    const _updateInvalidFieldsStyle = () => {
        if (!userDetails.email || !isValidEmail) {
            errInputs = addErrorInput("userEmailInput", errInputs);
            userDetails.verifiedEmail = false;
        } else {
            errInputs = removeErrorInput("userEmailInput", errInputs);
            userDetails.verifiedEmail = true;
        }

        // only check pwd when create or (edit and password inputs has input)
        if (
            !userDetails.id ||
            userDetails.password ||
            userDetails.confirmPassword
        ) {
            if (!userDetails.password || !isValidPwd)
                errInputs = addErrorInput("userPwdInput", errInputs);
            else errInputs = removeErrorInput("userPwdInput", errInputs);

            if (!userDetails.confirmPassword)
                errInputs = addErrorInput("userConfirmPwdInput", errInputs);
            else errInputs = removeErrorInput("userConfirmPwdInput", errInputs);

            if (!isPwdMatched) {
                errInputs = addErrorInput("userPwdInput", errInputs);
                errInputs = addErrorInput("userConfirmPwdInput", errInputs);
            }
        }

        if (!userDetails.fullName)
            errInputs = addErrorInput("userfullNameInput", errInputs);
        else errInputs = removeErrorInput("userfullNameInput", errInputs);

        if (!isValidPhone) {
            errInputs = addErrorInput("userPhoneInput", errInputs);
            userDetails.verifiedPhone = false;
        } else {
            errInputs = removeErrorInput("userPhoneInput", errInputs);
            userDetails.verifiedPhone = true;
        }

        if (isUserNewAuthenticatedByGoogle && !isValidQrCode)
            errInputs = addErrorInput("qrCodeInput", errInputs);
    };

    const _handleAuthenticatorTypeChange = async (e) => {
        const selectedMfa = parseInt(e.target.value);

        if (selectedMfa == 4) {
            await getMfaQrCode();

            if (qrCodeContent) {
                const qrCodeInput = document.getElementById("qrCodeInput");
                qrCodeInput.focus();
            }
        } else {
            qrCodeContent = "";
            qrCode = "";
        }
    };

    const _keepShowPasswordStatus = () => {
        let el = document.getElementById("userPwdInput");
        if (showPassword) el.type = "text";
        else el.type = "password";
    };

    const _toggleShowPassword = () => {
        showPassword = !showPassword;
        _keepShowPasswordStatus();
    };

    const _keepShowConfirmPasswordStatus = () => {
        let el = document.getElementById("userConfirmPwdInput");
        if (showConfirmPassword) el.type = "text";
        else el.type = "password";
    };

    const _toggleShowConfirmPassword = () => {
        showConfirmPassword = !showConfirmPassword;
        _keepShowConfirmPasswordStatus();
    };
</script>

<NotificationModal
    title={`${userDetails.id ? "Edit" : "Add"} User`}
    {isModalOpen}
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={closeModal}
    isConfirming={isModifyingUser}
    noContentPadding
    btnOptions={[
        {
            text: "Close",
            loading: isModifyingUser,
            class: "btn-error",
            function: () => closeModal(),
        },
        {
            text: userDetails.id ? "Update" : "Add",
            loading: isModifyingUser,
            function: () => checkInputValidation(),
        },
    ]}
>
    <div class="flex flex-1 flex-col gap-y-4 p-6 overflow-y-auto">
        <div class="flex items-start gap-x-4">
            <p class="title mandatory">Email Address</p>
            <div class="flex flex-col flex-1 gap-y-2">
                <input
                    type="text"
                    class="input input-sm input-bordered"
                    id="userEmailInput"
                    autocomplete="off"
                    disabled={userDetails.id}
                    bind:value={userDetails.email}
                    on:focus={() => resetDefaultStyle("userEmailInput")}
                />
                {#if userDetails.email && !isValidEmail}
                    <p class="text-error">Invalid Email Address</p>
                {/if}
            </div>
        </div>

        <div class="flex items-start gap-x-4">
            <p class="title mandatory">Password</p>
            <PasswordInput bind:value={userDetails.password} isRequired />
        </div>

        <div class="flex items-start gap-x-4">
            <p class="title mandatory">Confirm Password</p>
            <div class="flex flex-col flex-1 gap-y-2">
                <PasswordInput
                    bind:value={userDetails.confirmPassword}
                    pwdValue={userDetails.password}
                    isRequired
                    isConfirm
                />

                <!-- {#if !isPwdMatched}
                    <p class="text-error">Password does not match</p>
                {/if} -->
            </div>
        </div>

        <div class="flex items-center gap-x-4">
            <p class="title mandatory">Full Name</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                id="userfullNameInput"
                autocomplete="off"
                bind:value={userDetails.fullName}
                on:click={() => resetDefaultStyle("userfullNameInput")}
            />
        </div>

        <div class="flex items-start gap-x-4">
            <p class="title mandatory">Phone No.</p>
            <div class="flex flex-col flex-1 gap-y-2">
                <input
                    type="text"
                    class="input input-sm input-bordered"
                    id="userPhoneInput"
                    autocomplete="off"
                    bind:value={userDetails.phone}
                    on:focus={() => resetDefaultStyle("userPhoneInput")}
                />
                {#if userDetails.phone && !isValidPhone}
                    <p class="text-error">Invalid phone number</p>
                {/if}
            </div>
        </div>

        <div class="flex items-center gap-x-4">
            <p class="title">MFA type</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={userDetails.mfa}
                on:input={_handleAuthenticatorTypeChange}
            >
                <option value={Number(0)} selected>None</option>
                <option value={Number(2)}>SMS</option>
                {#if userDetails.id && userDetails.roleId == DirectUserRoles.Doctor}
                    <option value={Number(3)}>Mobile Notification</option>
                {/if}
                <!-- {#if userDetails.id}
                    <option value={Number(4)}>Google Authenticator</option>
                {/if} -->
            </select>
        </div>

        <!-- {#if isUserNewAuthenticatedByGoogle}
            <div class="flex flex-1 items-center">
                <p class="title" />

                <div class="flex flex-col gap-y-2 w-full max-w-xs">
                    {#if qrCodeContent}
                        <img src={qrCodeContent} alt="QR Code" />
                    {/if}
                    <input
                        type="text"
                        class="input input-sm input-bordered flex-1"
                        id="qrCodeInput"
                        placeholder="6-digits 2FA Code"
                        autocomplete="off"
                        bind:value={qrCode}
                    />
                </div>
            </div>
        {/if} -->
    </div>
</NotificationModal>

<style>
    .mandatory::after {
        content: " * ";
    }

    .title {
        line-height: 1.5rem;
        font-size: 1rem;
        font-weight: 700;
        min-width: 10rem;
    }
</style>
